import type { Pivotable } from '@empathyco/playboard-core-domain'
import type { Ref } from 'vue'
import type { FieldValue } from '../models'
import type { FieldValuesService } from '../services/interfaces'
import { ref } from 'vue'
import { CONTAINER } from '../config/inversify.config'
import { TYPES } from '../config/inversify.types'

export interface FieldValuesComposable {
  fieldValues: Ref<Record<string, Array<FieldValue>>>
  loadFieldValues: (params: FieldValuesComposableLoadParams) => Promise<Array<FieldValue>>
  reset: () => void
}

export interface FieldValuesComposableParams {
  reset?: boolean
}

export interface FieldValuesComposableLoadParams extends Pivotable {
  attribute: string
  isCategory?: boolean
}

const history = ref<Record<string, Array<FieldValue>>>({})

const fieldValues = ref<Record<string, Array<FieldValue>>>({})

export function useFieldValues(params: FieldValuesComposableParams = {}): FieldValuesComposable {
  const service = CONTAINER.get<FieldValuesService>(TYPES.FieldValuesService)

  async function loadFieldValues(
    params: FieldValuesComposableLoadParams,
  ): Promise<Array<FieldValue>> {
    const paramsKey = getFieldValuesLoadParamsKey(params)
    if (!history.value.hasOwnProperty(paramsKey)) {
      try {
        history.value[paramsKey] = await service.fetch({
          attribute: params.attribute,
          pivots: params.pivots.reduce<Record<string, string>>(
            (result, pivot) => ({ ...result, [pivot.name]: pivot.values[0] }),
            {},
          ),
          isCategory: params.isCategory ?? false,
        })
      } catch (error) {
        fieldValues.value = { ...fieldValues.value, [params.attribute]: [] }
        throw error
      }
    }
    fieldValues.value = {
      ...fieldValues.value,
      [params.attribute]: history.value[paramsKey],
    }
    return fieldValues.value[params.attribute]
  }

  function reset(): void {
    history.value = {}
    fieldValues.value = {}
  }

  if (params.reset) {
    reset()
  }

  return {
    fieldValues,
    loadFieldValues,
    reset,
  }
}

export function getFieldValuesLoadParamsKey(params: FieldValuesComposableLoadParams): string {
  return [
    `attribute=${params.attribute}`,
    params.pivots
      .reduce<
        Array<string>
      >((result, pivot) => [...result, `${pivot.name}=${pivot.values.slice().sort().join(',')}`], [])
      .sort(),
  ].join('&')
}
